'use client';

import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  autoUpdate,
  useClick,
  useFloating,
  useInteractions,
  FloatingFocusManager,
  useDismiss,
} from '@floating-ui/react';
import { StyleSheetManager } from 'styled-components';
import { AnimatePresence, type Variants } from 'framer-motion';

import Item from './Item';
import {
  ToggleBase,
  Curtain,
  Content,
  OverlayScrollbars,
} from './Toggle.styled';
import { useToggles } from './state';
import useWindowDimensions from '../utils/hooks';

export const variants: Variants = {
  open: {
    height: 'auto',
  },
  closed: {
    height: 0,
  },
};

export interface ToggleProps extends PropsWithChildren {
  hideTextOnMobile?: boolean;
  id: string;
  trackid: string;
  title: string;
  icon: ReactNode;
  badge?: number;
}

function Toggle({
  id,
  trackid,
  title,
  icon,
  badge,
  children,
  hideTextOnMobile,
}: ToggleProps) {
  const [animationComplete, setAnimationComplete] = useState<boolean>(false);
  const { openId, setOpenId } = useToggles();
  const { width } = useWindowDimensions();

  const open = openId === id;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: () => setOpenId(id),
    whileElementsMounted: autoUpdate,
    middleware: [
      {
        name: 'custom',
        fn({ x, y, rects }) {
          const trueX = x + rects.reference.width;

          return {
            x: trueX,
            y,
          };
        },
      },
    ],

    placement: 'left-start',
    strategy: 'absolute',
  });

  const click = useClick(context, { toggle: true });
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const { willChange, transform } = floatingStyles;

  const onAnimationStart = () => setAnimationComplete(false);
  const onAnimationComplete = () => setAnimationComplete(true);

  useEffect(() => {
    const { style } = document.body;
    if (open && width < 768) {
      style.overflow = 'hidden';
    } else {
      style.overflow = 'auto';
    }
  }, [open, width]);

  return (
    <ToggleBase>
      <Item
        data-trackid={trackid}
        title={title}
        icon={icon}
        badge={badge}
        aria-haspopup
        aria-expanded={false}
        ref={refs.setReference}
        hideTextOnMobile={hideTextOnMobile}
        {...getReferenceProps()}
      />
      <AnimatePresence>
        {open && (
          <FloatingFocusManager context={context}>
            <StyleSheetManager shouldForwardProp={() => true}>
              <Curtain
                layout
                layoutRoot
                variants={variants}
                initial="closed"
                animate="open"
                exit="closed"
                ref={refs.setFloating}
                onAnimationStart={onAnimationStart}
                onAnimationComplete={onAnimationComplete}
                {...getFloatingProps()}
              >
                <Item
                  active
                  title={title}
                  icon={icon}
                  aria-haspopup
                  aria-expanded
                  style={{ transform, willChange }}
                  hideTextOnMobile={hideTextOnMobile}
                  {...getReferenceProps()}
                />
                <Content style={{ transform, willChange }}>
                  <OverlayScrollbars
                    options={{
                      scrollbars: {
                        visibility: animationComplete ? 'auto' : 'hidden',
                      },
                    }}
                  >
                    {children}
                  </OverlayScrollbars>
                </Content>
              </Curtain>
            </StyleSheetManager>
          </FloatingFocusManager>
        )}
      </AnimatePresence>
    </ToggleBase>
  );
}

export * from './Toggle.styled';
export default Toggle;
